import { cn } from '@hbs-us/ui';

/**
 * Shared `_ops` block — skeleton loading rows (UI-SPEC §4 "Skeleton loading").
 *
 * Lists/tables show a token `bg-muted` shimmer on first load instead of a bare
 * spinner so the layout doesn't jump (A11Y-01: the region is `aria-busy` and
 * carries an accessible label; the shimmer bars are decorative). Every color is
 * a locked `@hbs-us/ui` token — no hex (BRAND-06). This is a presentation
 * primitive: it takes a row/column count, it does not fetch.
 */

export function SkeletonRows({
  rows = 6,
  columns = 1,
  label,
  className,
}: {
  /** Number of placeholder rows to render. */
  rows?: number;
  /** Number of shimmer bars per row (use the table's column count). */
  columns?: number;
  /**
   * Accessible label announced while loading (a t() string in app code). Leave it off to
   * render the rows as DECORATION inside a region that already announces itself — two
   * nested `role="status"` elements announce the same wait twice.
   */
  label?: string;
  className?: string;
}) {
  const bars = Array.from({ length: rows }, (_, rowIndex) => (
    <div
      // Skeleton rows are positional placeholders with no stable id.
      // biome-ignore lint/suspicious/noArrayIndexKey: positional shimmer placeholder
      key={rowIndex}
      className="flex items-center gap-4 rounded-md px-3 py-2"
    >
      {Array.from({ length: columns }, (_, colIndex) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: positional shimmer placeholder
          key={colIndex}
          className="h-4 flex-1 animate-pulse rounded-md bg-muted"
        />
      ))}
    </div>
  ));

  // The two branches are written out rather than folded into one element with a
  // conditional `role`. A conditional role reads as no role at all to the linter, which
  // then rejects the `aria-label` beside it — and it is genuinely harder to read.
  if (!label) {
    return (
      <div aria-hidden="true" className={cn('space-y-2', className)}>
        {bars}
      </div>
    );
  }

  return (
    <div role="status" aria-busy="true" aria-label={label} className={cn('space-y-2', className)}>
      {bars}
    </div>
  );
}
